import { useCallback, useEffect, useState } from "react";
import OperationsPageShell from "../components/OperationsPageShell";
import APICallService from "../../../../api/apiCallService";
import { operations } from "../../../../api/apiEndPoints";
import { operationsJSON } from "../../../../api/apiJSON/operations";
import { BranchHealthStatuses, IBranchHealthRow } from "../../../../types";

const branchPulseBadgeClassMap: Record<BranchHealthStatuses, string> = {
  [BranchHealthStatuses.Healthy]: "badge-light-success",
  [BranchHealthStatuses.Warning]: "badge-light-warning",
  [BranchHealthStatuses.Critical]: "badge-light-danger",
  [BranchHealthStatuses.NoActivity]: "badge-light-secondary",
};

const SummaryBoard = () => {
  const [branchPulse, setBranchPulse] = useState<IBranchHealthRow[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchBranchPulse = useCallback(async () => {
    setLoading(true);
    try {
      const apiService = new APICallService(
        operations.branchHealth,
        operationsJSON.branchHealth({})
      );
      const response = await apiService.callAPI();
      setBranchPulse(response?.records || []);
    } catch (err) {
      // Keep prior pulse data on transient failure; SummaryBoard is a lightweight overview.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranchPulse();
  }, [fetchBranchPulse]);

  return (
    <OperationsPageShell
      title="Summary Board"
      subtitle="Overview of alerts, branch pulse, demand hotspot, and fleet counters."
      action={
        <button type="button" className="uw-btn uw-btn-light uw-btn-sm btn btn-sm btn-light">
          ↻ Refresh
        </button>
      }
    >
      <div className="row g-5">
        {[1, 2, 3, 4].map((item) => (
          <div key={item} className="col-md-3 col-sm-6">
            <div className="card border h-100">
              <div className="card-body">
                <div className="text-muted small">KPI {item}</div>
                <div className="fs-2 fw-bold mt-2">{item * 12}</div>
                <div className="text-success small mt-2">
                  +8.4% vs last period
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-5 mt-1">
        <div className="col-lg-6">
          <div className="card border h-100">
            <div className="card-header border-0">
              <h4 className="mb-0">Critical alerts</h4>
            </div>
            <div className="card-body">
              <div className="d-flex justify-content-between mb-2">
                <span>Total</span>
                <strong>18</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Critical</span>
                <span className="badge badge-light-danger">7</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Warning</span>
                <span className="badge badge-light-warning">9</span>
              </div>
              <div className="d-flex justify-content-between">
                <span>Info</span>
                <span className="badge badge-light-info">2</span>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="card border h-100">
            <div className="card-header border-0">
              <h4 className="mb-0">Network pulse</h4>
            </div>
            <div className="card-body d-flex flex-wrap gap-2">
              {branchPulse.length === 0 && !loading && (
                <span className="text-muted fs-7">No branches to display.</span>
              )}
              {branchPulse.map((branch) => (
                <span
                  key={branch.branchId}
                  className={`badge ${
                    branchPulseBadgeClassMap[branch.status] ||
                    "badge-light-secondary"
                  } px-3 py-2`}
                >
                  {branch.branchName}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </OperationsPageShell>
  );
};

export default SummaryBoard;
